import { LinkButton, Loading, Section, SubmitButton } from "@dgs/core";
import { Form, Formik } from "formik";
import { FC, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { NavigateWithQueryParams } from "../../NavigateWithQueryParams";
import { useRegion } from "../../region/RegionProvider";
import { ConsentFormFields } from "../consents/ConsentFormFields";
import { ConsentValues, useRegistrationForm } from "../provider/registrationFormProviderContext";
import { findNextStep, toConsentErrors, toConsentValues } from "../registrationFormUtils";
import { RenderRegistrationBlock } from "../RenderRegistrationBlock";

const StyledSection = styled(Section)`
	padding: ${({ theme }) => theme.spacing(4)};
`;

const ButtonWrapper = styled.div`
	display: flex;
	justify-content: flex-end;
	padding: 0 0 ${({ theme }) => theme.spacing(4)} 0;
	gap: ${({ theme }) => theme.spacing(4)};
`;

export const RegistrationLandingPage: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const {
		registrationForm,
		hiddenSteps,
		submitLanding,
		givenConsents,
		landingConsents,
		areConsentsLoading,
		setHasSeenLanding,
	} = useRegistrationForm();
	const isRegistered = registrationForm.guest.isRegistered;
	const noLandingBlockAndConsents = registrationForm.landingBlockId === null && !landingConsents.length;
	const { regionId } = useRegion();
	useEffect(() => {
		setHasSeenLanding(true);
	}, [setHasSeenLanding]);

	const onSubmit = useCallback(
		async (values: ConsentValues) => {
			const nextStepId = submitLanding(values);
			navigate(`../${nextStepId}`);
		},
		[navigate, submitLanding],
	);

	if (areConsentsLoading) {
		return (
			<StyledSection color="content">
				<Loading />
			</StyledSection>
		);
	}

	if (noLandingBlockAndConsents) {
		const nextStepId = findNextStep(hiddenSteps, registrationForm.steps, null);
		return <NavigateWithQueryParams to={`../${nextStepId}`} replace={true} />;
	}

	return (
		<>
			{registrationForm.landingBlockId && <RenderRegistrationBlock blockId={registrationForm.landingBlockId} />}
			<StyledSection color="content">
				<Formik
					initialValues={toConsentValues(landingConsents, givenConsents)}
					onSubmit={onSubmit}
					validate={(values: ConsentValues) => toConsentErrors(landingConsents, values, t)}
				>
					<Form>
						{landingConsents.length > 0 && <ConsentFormFields consents={landingConsents} />}
						<ButtonWrapper>
							{isRegistered
								? registrationForm.isCancelable && <LinkButton to="../cancel">{t("registration.cancel")}</LinkButton>
								: registrationForm.isDeclinable && <LinkButton to="../decline">{t("registration.decline")}</LinkButton>}
							{registrationForm.isReplacementEnabled && registrationForm.guest.id !== "new" && (
								<LinkButton to="../nominate-replacement">{t("Nominate replacement")}</LinkButton>
							)}
							{(registrationForm.isEditable || !isRegistered) && (
								<SubmitButton disabled={!regionId}>
									{t(isRegistered ? "registration.update" : "registration.register")}
								</SubmitButton>
							)}
						</ButtonWrapper>
					</Form>
				</Formik>
			</StyledSection>
		</>
	);
};
